import moment from 'moment-timezone';

export async function exportToXlsx(fileName: string, tableData: any, tableColumns: any) {
	const [{ utils, write  }, { head, body }] = await Promise.all([
		import('xlsx'),
		getGridContent(tableData, tableColumns)
	]);
	const wb = utils.book_new();
	const ws = utils.aoa_to_sheet([...head, ...body]);
	const wscols = [];
	for (const cell of head[0]) {
		wscols.push({ wch: parseInt(cell.length) + 10 });
	}
	ws['!cols'] = wscols;
	utils.book_append_sheet(wb, ws, 'Sheet 1');

	// SaveAs dialogue
	const wbout = write(wb, { bookType: 'xlsx', type: 'array' });
    if ('showSaveFilePicker' in window) {
        try {
            const fileHandle = await (window as any).showSaveFilePicker({
                suggestedName: fileName,
                types: [{ description: 'Excel File', accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] } }]
            });
            const writable = await fileHandle.createWritable();
            await writable.write(new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
            await writable.close();
        } catch (error) {
            console.error("File save error:", error);
        }
    } else {
        const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
}

function getColorTextRepresentation(trendData: string) {
    if (!trendData) return '';

    const colorMap: { [key: string]: string } = {
        '1': "Success",
        '2': "Failed",
        '3': "In Progress",
        '4': "Long Running",
        '5': "Delayed",
		'6': "--",
        '7': "Scheduled"
    };

    return trendData.split('').map(char => colorMap[char] || char).join(' ');
}

const getHrsMin = (totalMinutes: number) => {
	totalMinutes = totalMinutes / 60;
	const days = Math.floor(totalMinutes / (24 * 60));
	const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
	const minutes = totalMinutes % 60;

	// Format the result
	let result = '';
	if (days > 0) result += `${days} day${days > 1 ? 's' : ''} `;
	if (hours > 0) result += `${hours} hr${hours > 1 ? 's' : ''} `;
	if (minutes > 0) result += `${minutes.toFixed(2)} min`;

	let time = result.trim()
	return time
}

const dateHandler = (row: any[], x: any, key: string) => {
  row.push(x[key] ? moment(x[key]).format() : '');
};

const dashHandler = (row: any[], x: any, key: string) => {
  row.push(x[key] ? x[key] : '--');
};

const sectorsHandler = (row: any[], x: any, key: string) => {
  row.push(x[key] ? x[key].split(', ').join(', ') : '--');
};

const slaTimeHandler = (row: any[], x: any) => {
  const scheduleDate = x['Schedule_Date']
    ? x['Schedule_Date'].split('T')[0]
    : '';

  if (x['SLA_Date'] != null && x['SLA_Date'] > 0) {
    row.push(
      moment(scheduleDate)
        .add(x['SLA_Date'], 'days')
        .format('MM-DD-YY')
    );
  } else {
    row.push(moment(scheduleDate).format('MM-DD-YY'));
  }

  if (x['SLA_Time']) {
    const slaTime = x['SLA_Time']
      .toString()
      .padStart(4, '0')
      .replace(/(.{2})$/, ':$1');
    row[row.length - 1] += ` ${slaTime}`;
  } else {
    row[row.length - 1] += ' --';
  }
};

const actualCompletionHandler = (row: any[], x: any, key: string) => {
  if (!x[key]) {
    row.push('--');
    return;
  }

  const [datePart, timePart] = x[key].split('T');
  const [year, month, day] = datePart.split('-');
  const [hour, minute, secondWithMs] = timePart.split(':');
  const [second] = secondWithMs.split('.');

  row.push(`${month}-${day}-${year} ${hour}:${minute}:${second}`);
};

const orderDateHandler = (row: any[], x: any) => {
  row.push(x['orderDate'] ? moment(x['orderDate']).format() : '');
};

const platformHandler = (row: any[], x: any, key: string) => {
  row.push(x[key] == 1 ? 'Teradata' : '');
};

const cyclicHandler = (row: any[], x: any, key: string) => {
  let value;

  if (x[key] == '0') {
    value = 'No';
  } else if (x[key] == '1') {
    value = 'Yes';
  } else {
    value = x[key];
  }

  row.push(value);
};

const yesNoHandler = (row: any[], x: any, key: string) => {
  row.push(x[key] ? 'Yes' : 'No');
};

const trendHandler = (row: any[], x: any, key: string) => {
  row.push(getColorTextRepresentation(x[key]));
};

const tzDateHandler = (row: any[], x: any, key: string) => {
  row.push(
    x[key]
      ? moment(x[key])
          .tz('America/Chicago')
          .format('MM-DD-YY HH:mm:ss')
      : '--'
  );
};

const avgRunTimeHandler = (row: any[], x: any, key: string) => {
  row.push(x[key] ? getHrsMin(Number(x[key])) : '--');
};

const totalExecutionsHandler = (row: any[], x: any, key: string) => {
  let value;

  if (x['jobStatus'] === 'Not Scheduled' || x['jobStatus'] === 'Scheduled') {
    value = 0;
  } else if (x[key]) {
    value = x[key];
  } else {
    value = '--';
  }

  row.push(value);
};

const columnHandlers: Record<
	string,
	(row: any[], x: any, key: string) => void
	> = {
	runStartTime: dateHandler,
	runEndTime: dateHandler,
	createdAt: dateHandler,
	deletedAt: dateHandler,
	lastSuccessRun: dateHandler,

	Sectors: sectorsHandler,

	IP_Delayed_Start: dashHandler,
	IP_Long_Running: dashHandler,
	Failed: dashHandler,
	Success: dashHandler,

	SLA_Time: (row, x) => slaTimeHandler(row, x),

	Actual_Completion_Time: actualCompletionHandler,

	oDate: (row, x) => orderDateHandler(row, x),

	platformId: platformHandler,
	isCyclic: cyclicHandler,
	criticalFlag: yesNoHandler,

	execStatusTrend: trendHandler,

	actualStartTime: tzDateHandler,
	actualEndTime: tzDateHandler,

	avgRunTime: avgRunTimeHandler,

	totalExecutions: totalExecutionsHandler,
};

async function getGridContent(tableData: any, tableColumns: any) {
  const extractedColumns = [tableColumns.map((x: any) => x.name)];
  const columnKeys = tableColumns.map((x: any) => x.key);

  const extractedRows = tableData.map((x: any) => {
    const row: any[] = [];

    columnKeys.forEach((key: string) => {
      const handler = columnHandlers[key];

      if (handler) {
        handler(row, x, key);
      } else {
        row.push(x[key]);
      }
    });

    return row;
  });

  return {
    head: extractedColumns,
    body: extractedRows,
  };
}

